import { Blocks, Check, Loader2, LogIn, UserMinus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { AvatarPersonne } from '@/components/ui/AvatarPersonne'
import { useProfile } from '@/api/profile'
import {
  useTeamAccess,
  useUpdateMemberAccess,
  type InterfaceAgent,
  type RecordingAccessChoice,
} from '@/api/team'
import type { AccessRole } from '@/api/auth'
import { cn } from '@/lib/utils'

type ChosenRole = Exclude<AccessRole, 'owner'>
const ROLES: ChosenRole[] = ['manager', 'supervisor', 'member']
const ACCES: RecordingAccessChoice[] = ['default', 'all', 'own', 'none']

interface Props {
  agent: InterfaceAgent | null
  onClose: () => void
  onRemove: (agent: InterfaceAgent) => void
  onGrantAccess: (agent: InterfaceAgent) => void
}

export function fullName(agent: InterfaceAgent): string {
  return `${agent.user_first_name || ''} ${agent.user_last_name || ''}`.trim()
}

export function roleOf(agent: InterfaceAgent): AccessRole {
  return agent.access_role ?? (agent.role === 'MANAGER' ? 'owner' : 'member')
}

/**
 * One team member: role, recording exception, removal. A side panel on a
 * computer, the whole screen on a phone. Each change saves at once, as before.
 * The owner opens read-only; only the owner names or removes a manager;
 * nobody changes their own role.
 */
export function MemberSheet({ agent, onClose, onRemove, onGrantAccess }: Props) {
  return (
    <Sheet open={!!agent} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-none sm:w-[440px] sm:max-w-[440px] p-0 flex flex-col gap-0"
      >
        {agent && <Fiche agent={agent} onRemove={onRemove} onGrantAccess={onGrantAccess} />}
      </SheetContent>
    </Sheet>
  )
}

function Fiche({ agent, onRemove, onGrantAccess }: Omit<Props, 'agent' | 'onClose'> & { agent: InterfaceAgent }) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const { data: etat } = useTeamAccess()
  const update = useUpdateMemberAccess()

  const role = roleOf(agent)
  const soiMeme = profile?.id === agent.user_id
  const peutChanger = !!etat?.can_edit && role !== 'owner' && !soiMeme
    && (role !== 'manager' || !!etat?.can_name_managers)
  const acces = agent.recording_access ?? 'default'
  const regle = etat?.rule ?? 'own'
  const rattache = !!agent.external_id
  const retirable = !!etat?.can_edit && !soiMeme && (role === 'supervisor' || role === 'member')
  const nom = fullName(agent) || agent.user_email

  const envoyer = (input: { role?: ChosenRole; recording_access?: RecordingAccessChoice }) =>
    update.mutate({ profileId: agent.id, ...input }, {
      onSuccess: () => toast.success(t('recordingAccess.memberSaved', { name: agent.user_first_name || agent.user_email })),
      onError: () => toast.error(t('recordingAccess.saveError')),
    })

  const roles: AccessRole[] = role === 'owner' ? ['owner'] : ROLES
  const libelleAcces = (a: RecordingAccessChoice) =>
    a === 'default' ? t('recordingAccess.exception.default') : t(`recordingAccess.exception.${a}`)

  return (
    <>
      <div className="px-6 pt-6 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 pr-8">
          <AvatarPersonne nom={nom} photo={agent.avatar_url} className="w-12 h-12" />
          <div className="min-w-0">
            <SheetTitle className="text-base truncate">{nom}</SheetTitle>
            <SheetDescription className="truncate">{agent.user_email}</SheetDescription>
          </div>
        </div>
        {rattache && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400" title={t('manager.team.list.linkedHelp')}>
            <Blocks className="w-3.5 h-3.5" />
            {t('manager.team.list.linkedVia', { name: agent.linked_via || 'API' })}
            {agent.phone_verified === false && (
              <span className="text-amber-600 dark:text-amber-400">· {t('manager.team.list.phoneToProve')}</span>
            )}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
        <section>
          <div className="flex items-center gap-2 mb-2.5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('recordingAccess.roleLabel')}</h3>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          </div>
          <div role="radiogroup" aria-label={t('recordingAccess.roleLabel')} className="space-y-2">
            {roles.map((r) => {
              const actif = r === role
              const bloque = !peutChanger || update.isPending || (r === 'manager' && !etat?.can_name_managers)
              return (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={actif}
                  disabled={bloque && !actif}
                  onClick={() => !actif && !bloque && envoyer({ role: r as ChosenRole })}
                  className={cn(
                    'w-full text-left rounded-lg border px-3.5 py-2.5 flex items-start gap-3 transition-colors',
                    actif
                      ? 'border-indigo-500 bg-indigo-50/60 dark:border-indigo-400 dark:bg-indigo-500/10'
                      : 'border-slate-200 dark:border-slate-700 enabled:hover:border-slate-300 enabled:hover:bg-slate-50 dark:enabled:hover:bg-slate-800/50 disabled:opacity-50',
                    actif && bloque && 'cursor-default',
                  )}
                >
                  <span className={cn(
                    'mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center flex-shrink-0',
                    actif ? 'border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400' : 'border-slate-300 dark:border-slate-600',
                  )}>
                    {actif && <Check className="h-3 w-3 text-white dark:text-slate-900" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900 dark:text-white">{t(`recordingAccess.roles.${r}.label`)}</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">{t(`recordingAccess.roles.${r}.description`)}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </section>

        <section>
          <label htmlFor={`acces-${agent.id}`} className="block text-sm font-semibold text-slate-900 dark:text-white mb-2.5">
            {t('recordingAccess.exceptionLabel')}
          </label>
          {role === 'member' ? (
            <Select
              value={acces}
              onValueChange={(v) => envoyer({ recording_access: v as RecordingAccessChoice })}
              disabled={!peutChanger || update.isPending}
            >
              <SelectTrigger id={`acces-${agent.id}`} className="h-10 w-full">
                <SelectValue>
                  {acces === 'default'
                    ? `${libelleAcces('default')} · ${t(`recordingAccess.rule.${regle}`)}`
                    : libelleAcces(acces)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ACCES.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a === 'default' ? `${libelleAcces('default')} · ${t(`recordingAccess.rule.${regle}`)}` : libelleAcces(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            // Owner, managers and supervisors hear every call: nothing to choose.
            <Select value="all" disabled>
              <SelectTrigger id={`acces-${agent.id}`} className="h-10 w-full">
                <SelectValue>{t('recordingAccess.exception.all')}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('recordingAccess.exception.all')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </section>

        {rattache && agent.dashboard_access === false && (
          <Button type="button" variant="outline" onClick={() => onGrantAccess(agent)} title={t('manager.team.list.dashboardAccessHelp')}>
            <LogIn className="w-4 h-4 mr-2" />
            {t('manager.team.list.dashboardAccess')}
          </Button>
        )}
      </div>

      {retirable && (
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onRemove(agent)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 px-2"
          >
            <UserMinus className="w-4 h-4 mr-2" />
            {t('recordingAccess.remove')}
          </Button>
        </div>
      )}
    </>
  )
}
